import React, { useState, useEffect } from 'react';
import './PodcastList.css';
import { Link } from 'react-router-dom';
import { fetchPodcastPage } from '../utils/api';
import { formatContentDate } from '../utils/date';

const PAGE_SIZE = 12;

const PodcastArtwork = ({ podcast }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const imageIcon = podcast.icon?.type === 'file' || podcast.icon?.type === 'external';

  if (podcast.icon?.type === 'emoji') {
    return (
      <span className="show-icon emoji-icon" aria-hidden="true">
        {podcast.icon.emoji}
      </span>
    );
  }

  if (!imageIcon) {
    return null;
  }

  if (!imageFailed) {
    return (
      <img
        src={podcast.icon.url}
        alt=""
        className="show-icon"
        width="40"
        height="40"
        loading="lazy"
        decoding="async"
        onError={() => setImageFailed(true)}
      />
    );
  }

  return (
    <span className="show-icon emoji-icon" aria-hidden="true">
      🎙️
    </span>
  );
};

const PodcastList = () => {
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [bufferedPodcasts, setBufferedPodcasts] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchPodcasts = async () => {
      try {
        const page = await fetchPodcastPage({
          limit: PAGE_SIZE,
          signal: controller.signal,
        });

        setPodcasts(page.podcasts);
        setNextCursor(page.nextCursor);
        setBufferedPodcasts(page.remainingPodcasts);
        setHasMore(
          page.remainingPodcasts.length > 0 ||
          (page.hasMore && Boolean(page.nextCursor))
        );
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchPodcasts();

    return () => controller.abort();
  }, []);

  const loadMorePodcasts = async () => {
    if ((!nextCursor && bufferedPodcasts.length === 0) || loadingMore) {
      return;
    }

    setLoadingMore(true);
    setLoadMoreError(null);

    try {
      if (bufferedPodcasts.length > 0) {
        const nextPodcasts = bufferedPodcasts.slice(0, PAGE_SIZE);
        const remainingPodcasts = bufferedPodcasts.slice(PAGE_SIZE);

        setPodcasts((currentPodcasts) => [...currentPodcasts, ...nextPodcasts]);
        setBufferedPodcasts(remainingPodcasts);
        setHasMore(remainingPodcasts.length > 0 || Boolean(nextCursor));
        return;
      }

      const page = await fetchPodcastPage({
        limit: PAGE_SIZE,
        cursor: nextCursor,
      });

      setPodcasts((currentPodcasts) => {
        const currentIds = new Set(currentPodcasts.map((podcast) => podcast.id));
        const newPodcasts = page.podcasts.filter((podcast) => !currentIds.has(podcast.id));
        return [...currentPodcasts, ...newPodcasts];
      });
      setNextCursor(page.nextCursor);
      setBufferedPodcasts(page.remainingPodcasts);
      setHasMore(
        page.remainingPodcasts.length > 0 ||
        (page.hasMore && Boolean(page.nextCursor))
      );
    } catch (err) {
      setLoadMoreError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <section className="podcast-container" aria-labelledby="podcast-archive-title" aria-busy="true">
        <h3 id="podcast-archive-title" className="listen-title">Recent Podcast Notes</h3>
        <p className="podcast-status" role="status" aria-live="polite">
          Loading podcast notes…
        </p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="podcast-container" aria-labelledby="podcast-archive-title">
        <h3 id="podcast-archive-title" className="listen-title">Recent Podcast Notes</h3>
        <p className="podcast-status podcast-error" role="alert">
          {error} Please try again later.
        </p>
      </section>
    );
  }

  return (
    <section className="podcast-container" aria-labelledby="podcast-archive-title">
      <h3 id="podcast-archive-title" className="listen-title">Recent Podcast Notes</h3>
      <ul id="podcast-archive-list" className="podcast-list">
        {podcasts.map((podcast) => (
          <li key={podcast.id} className="podcast-item">
            <div className="podcast-icon">
              <PodcastArtwork podcast={podcast} />
            </div>
            <div className="podcast-main">
              <h4 className="podcast-title">{podcast.title || 'Untitled episode'}</h4>
              <div className="podcast-meta">
                {podcast.date ? (
                  <time className="date" dateTime={podcast.date}>
                    {formatContentDate(podcast.date)}
                  </time>
                ) : (
                  <span className="date">No date</span>
                )}
                {podcast.show && <span className="show">{podcast.show}</span>}
                {podcast.snips > 0 && <span className="snips">Notes: {podcast.snips}</span>}
              </div>
            </div>
            {podcast.showNotesLink && (
              <div className="podcast-links">
                <Link
                  to={`/podcasts/${podcast.id}`}
                  className="podcast-link"
                  aria-label={`View notes for ${podcast.title || 'this episode'}`}
                >
                  View Notes →
                </Link>
              </div>
            )}
          </li>
        ))}
      </ul>

      <div className="podcast-archive-controls">
        <p className="podcast-results-status" aria-live="polite">
          {hasMore
            ? `Showing ${podcasts.length} podcast notes.`
            : `All ${podcasts.length} podcast notes are shown.`}
        </p>
        {loadMoreError && (
          <p className="podcast-status podcast-error" role="alert">
            {loadMoreError} You can retry below.
          </p>
        )}
        {hasMore && (
          <button
            type="button"
            className="podcast-load-more"
            onClick={loadMorePodcasts}
            disabled={loadingMore}
            aria-controls="podcast-archive-list"
          >
            {loadingMore ? 'Loading more…' : 'Load more podcast notes'}
          </button>
        )}
      </div>
    </section>
  );
};

export default PodcastList;
